import React, { useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type { ChangeAgendaEmailVariable } from '../../../types/changes';

const FALLBACK_VARIABLES: ChangeAgendaEmailVariable[] = [
  {
    token: '{{mudanca.titulo}}',
    key: 'mudanca.titulo',
    group: 'mudanca',
    group_label: 'Dados da mudança',
    label: 'Título da mudança',
    description:
      'Nome da mudança desta pauta. Se o e-mail tiver várias mudanças, os títulos entram separados por ponto e vírgula.',
    example: 'Atualização do gateway de pagamentos',
    aliases: '{{mudanca.name}}',
  },
  {
    token: '{{mudanca.descricao}}',
    key: 'mudanca.descricao',
    group: 'mudanca',
    group_label: 'Dados da mudança',
    label: 'Descrição da mudança',
    description:
      'Texto da descrição cadastrada na mudança. Com várias mudanças, as descrições entram uma abaixo da outra.',
    example: 'Troca do certificado do endpoint de conciliação, sem alteração de regra de negócio.',
    aliases: '{{mudanca.description}}',
  },
  {
    token: '{{pauta_html}}',
    key: 'pauta_html',
    group: 'pauta',
    group_label: 'Dados da pauta',
    label: 'Lista formatada da pauta',
    description: 'HTML pronto com as mudanças da pauta. Use no corpo, não no assunto.',
    example: '<h3>Pauta Comitê…</h3>',
  },
];

interface Props {
  variables?: ChangeAgendaEmailVariable[];
  onInsertSubject?: (token: string) => void;
  onInsertBody?: (token: string) => void;
  defaultOpen?: boolean;
}

const AgendaEmailVariablesHelp: React.FC<Props> = ({
  variables,
  onInsertSubject,
  onInsertBody,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [loaded, setLoaded] = useState<ChangeAgendaEmailVariable[]>(variables || []);

  useEffect(() => {
    if (variables?.length) {
      setLoaded(variables);
      return;
    }
    let cancelled = false;
    void changesApi
      .listAgendaEmailVariables()
      .then((response) => {
        if (!cancelled && response.items?.length) setLoaded(response.items);
      })
      .catch(() => {
        if (!cancelled) setLoaded(FALLBACK_VARIABLES);
      });
    return () => {
      cancelled = true;
    };
  }, [variables]);

  const groups = useMemo(() => {
    const source = loaded.length ? loaded : FALLBACK_VARIABLES;
    const order: string[] = [];
    const buckets: Record<string, { label: string; items: ChangeAgendaEmailVariable[] }> = {};
    for (const item of source) {
      if (!buckets[item.group]) {
        buckets[item.group] = { label: item.group_label, items: [] };
        order.push(item.group);
      }
      buckets[item.group].items.push(item);
    }
    return order.map((key) => buckets[key]);
  }, [loaded]);

  return (
    <div className="mb-3">
      <Button
        size="sm"
        variant="outline-secondary"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? 'Ocultar variáveis' : 'Inserir variáveis no texto'}
      </Button>
      {open ? (
        <div className="border rounded p-2 mt-2 bg-light">
          <p className="small text-muted mb-2">
            O envio troca o texto entre chaves pelo valor real. Também vale{' '}
            <code>{'{{mudanca.name}}'}</code> e <code>{'{{mudanca.description}}'}</code>.
          </p>
          {groups.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="small fw-semibold">{group.label}</div>
              <Table size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Variável</th>
                    <th>Significado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => (
                    <tr key={item.token}>
                      <td>
                        <code title={item.example || item.description}>{item.token}</code>
                        {item.aliases ? (
                          <div className="small text-muted">também {item.aliases}</div>
                        ) : null}
                      </td>
                      <td className="small" title={item.description}>
                        {item.label}
                      </td>
                      <td className="text-nowrap">
                        {onInsertSubject ? (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            onClick={() => onInsertSubject(item.token)}
                          >
                            No título
                          </Button>
                        ) : null}
                        {onInsertBody ? (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => onInsertBody(item.token)}
                          >
                            Na descrição
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AgendaEmailVariablesHelp;
